import React, { useState, useEffect } from 'react';
import { ArrowRight, Phone } from 'lucide-react';

const WelcomePage = ({ onEnterSite }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Any interaction (scroll, touch, mousemove) enters the site
  useEffect(() => {
    const enter = (e) => {
      // Don't trigger on phone link
      if (e.target.closest('[data-testid="welcome-phone"]')) return;
      onEnterSite();
    };
    window.addEventListener('wheel', enter, { once: true, passive: true });
    window.addEventListener('touchmove', enter, { once: true, passive: true });
    return () => {
      window.removeEventListener('wheel', enter);
      window.removeEventListener('touchmove', enter);
    };
  }, [onEnterSite]);

  return (
    <div
      className="relative min-h-screen w-full bg-[#0a1628] overflow-hidden flex flex-col cursor-pointer select-none"
      onClick={onEnterSite}
    >
      {/* Background video with heavy overlay for elegance */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      >
        <source
          src="https://customer-assets.emergentagent.com/job_sealwash/artifacts/ecggi8bu_f374f72d-a370-4b56-8173-48b38de12a54.mp4"
          type="video/mp4"
        />
      </video>

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/70 via-[#0a1628]/40 to-[#0a1628]/80" />

      {/* Top bar - phone only (no logo here) */}
      <header
        className={`relative z-10 flex items-center justify-end px-6 md:px-12 py-5 md:py-6 transition-all duration-1000 ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}
      >
        <a
          href="tel:8192002015"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          data-testid="welcome-phone"
        >
          <Phone size={16} className="text-cyan-400" />
          <span className="text-sm md:text-base font-medium tracking-wide">819.200.2015</span>
        </a>
      </header>

      {/* Main content - centered */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 -mt-10">
        {/* Decorative line */}
        <div
          className={`w-12 h-[2px] bg-gradient-to-r from-cyan-400 to-blue-500 mb-8 transition-all duration-1000 delay-300 ${
            loaded ? 'opacity-100 w-12' : 'opacity-0 w-0'
          }`}
        />

        {/* Logo central with soft white glow */}
        <div
          className={`text-center transition-all duration-1000 delay-500 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="relative">
            <img
              src="/logo-glf-new.png"
              alt="Groupe Lavage Fenêtres"
              className="relative h-96 sm:h-[30rem] md:h-[44rem] w-auto mx-auto opacity-90"
              data-testid="welcome-center-logo"
            />
          </div>
        </div>

        {/* Tagline */}
        <p
          className={`text-gray-400 text-sm md:text-lg text-center mt-6 max-w-md leading-relaxed tracking-wide transition-all duration-1000 delay-700 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Service professionnel de lavage de fenêtres et bâtiments au Québec
        </p>

        {/* CTA Button */}
        <button
          data-testid="enter-site-button"
          onClick={onEnterSite}
          className={`group mt-10 md:mt-12 flex items-center gap-3 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 hover:border-cyan-400/60 text-cyan-400 hover:text-cyan-300 px-8 md:px-10 py-3.5 md:py-4 rounded-full transition-all duration-500 delay-1000 backdrop-blur-sm ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="text-sm md:text-base font-medium tracking-wide">Visiter notre site</span>
          <ArrowRight
            size={18}
            className="text-cyan-400 group-hover:translate-x-1 transition-transform duration-300"
          />
        </button>

        {/* Trust indicators */}
        <div
          className={`flex items-center gap-6 mt-10 text-gray-500 text-xs md:text-sm tracking-wide transition-all duration-1000 delay-[1200ms] ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span>5 ans d'expérience</span>
          <span className="w-1 h-1 rounded-full bg-cyan-400/50" />
          <span>Centre du Québec</span>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="relative z-10 flex justify-center pb-8">
        <div
          className={`w-16 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent transition-all duration-1000 delay-[1400ms] ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>
    </div>
  );
};

export default WelcomePage;
